import { useEffect, useRef } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

interface UsePaymentCleanupOptions {
  paymentIntentId: string | null;
  isPaymentComplete: boolean;
  enabled?: boolean;
}

/**
 * Hook pour annuler automatiquement un PaymentIntent Stripe
 * quand l'utilisateur quitte la page de paiement sans avoir payé.
 * 
 * Détecte :
 * - Fermeture de l'onglet/fenêtre (beforeunload)
 * - Navigation arrière du navigateur (popstate)
 * - Changement d'onglet avec retour (visibilitychange)
 * - Démontage du composant (cleanup)
 * 
 * @param paymentIntentId - ID du PaymentIntent Stripe à annuler
 * @param isPaymentComplete - true si le paiement est terminé (succès ou échec)
 * @param enabled - Active/désactive le hook (défaut: true)
 */
export function usePaymentCleanup({
  paymentIntentId,
  isPaymentComplete,
  enabled = true,
}: UsePaymentCleanupOptions) {
  const cleanupAttemptedRef = useRef(false);
  const visibilityTimerRef = useRef<number | null>(null);
  // Skip first cleanup in dev (React StrictMode mount/unmount/remount cycle)
  const skipCleanupRuns = useRef(import.meta.env.DEV ? 1 : 0);

  useEffect(() => {
    if (!enabled || !paymentIntentId || isPaymentComplete) {
      return;
    }
    // Reset skip counter when paymentIntentId changes (new payment)
    cleanupAttemptedRef.current = false;

    const cancelPaymentIntent = async () => {
      // Éviter les appels multiples
      if (cleanupAttemptedRef.current) return;
      cleanupAttemptedRef.current = true;

      try {
        // Utiliser sendBeacon pour garantir l'envoi même si la page se ferme
        const url = `${API_BASE_URL}/api/payments/cancel-intent/${paymentIntentId}`;
        const blob = new Blob([JSON.stringify({})], { type: 'application/json' });
        
        // sendBeacon est plus fiable que fetch lors de la fermeture de page
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, blob);
        } else {
          // Fallback pour les navigateurs qui ne supportent pas sendBeacon
          await fetch(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            keepalive: true, // Permet à la requête de continuer après fermeture de page
          });
        }
      } catch (error) {
        console.error('Erreur lors de l\'annulation du PaymentIntent:', error);
      }
    };

    // 1. Détection de la fermeture de l'onglet/fenêtre
    const handleBeforeUnload = () => {
      cancelPaymentIntent();
      // Ne pas afficher de message de confirmation (mauvaise UX)
    };

    // 2. Détection de la navigation arrière
    const handlePopState = () => {
      cancelPaymentIntent();
    };

    // 3. Détection du changement d'onglet (l'utilisateur part et ne revient pas)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // L'utilisateur a quitté l'onglet, attendre 5 minutes
        visibilityTimerRef.current = setTimeout(() => {
          // Si après 5 minutes l'utilisateur n'est pas revenu, annuler
          if (document.hidden && !isPaymentComplete) {
            cancelPaymentIntent();
          }
        }, 300000); // 5 minutes (300 secondes)
      } else {
        // L'utilisateur est revenu, annuler le timer
        if (visibilityTimerRef.current) {
          clearTimeout(visibilityTimerRef.current);
          visibilityTimerRef.current = null;
        }
      }
    };

    // Attacher les listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup au démontage du composant
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
      }

      // Si le composant se démonte et que le paiement n'est pas terminé, annuler
      // Skip first unmount in dev (StrictMode double-mount)
      if (skipCleanupRuns.current > 0) {
        skipCleanupRuns.current -= 1;
        return;
      }
      if (!isPaymentComplete && !cleanupAttemptedRef.current) {
        cancelPaymentIntent();
      }
    };
  }, [paymentIntentId, isPaymentComplete, enabled]);

  // Fonction pour marquer manuellement le paiement comme terminé
  const markPaymentComplete = () => {
    cleanupAttemptedRef.current = true;
  };

  return { markPaymentComplete };
}
